import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { TO_CENTS, ProjectDTO, TO_DOLLARS, BUILD_LANE, FORMAT_LOCATION } from 'cargo-chief-commons';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from '@state/core.state';
import { catchError, map, mergeMap, switchMap, of, take, debounceTime } from 'rxjs';
import { withLatestFrom, timeout } from 'rxjs/operators';

import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { ProjectDetailsService } from '@service/projectDetails/project-details.service';
import {
	CARGOCHIEF_KEYS,
	RANGE_FILTERS,
	QUALITY_SCORE_DISPLAY,
} from '@domain/constants/app-variables';
import { environment } from '@environment/environment';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { CapacitySearchFilterService } from '@service/capacitySearchFilter/capacity-search-filter.service';
import { CsvExportService } from '@service/csv-export/csv-export.service';
import { ConfirmationDialogComponent } from '../../../common/confirmation-dialog/confirmation-dialog.component';

import {
	DEFAULT_SYSTEM_GROUP_NAME,
	DEFAULT_SYSTEM_GROUP_DELAY,
	IS_WORKFLOW_PROJECT_TYPE,
} from '@domain/constants/project-types';
import { ALLOCATED_CARRIERS_GROUP_NAME } from '@service/project-group/project-group.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';

import {
	RestService,
	IS_NOT_NULL_OR_UNDEFINED,
	PROJECT_CARRIER_MAP_STATUS,
	ProjectGroup,
	IS_NULL_OR_UNDEFINED,
	TruckSearchTable,
	IS_NOT_EMPTY,
	ProjectCarrierMapDTO,
	DELAY_OPTIONS,
	IS_EMPTY,
	GET_LANE_PATH_STRING,
	GET_PROJECT_EXPORT_CONTACT,
} from 'cargo-chief-commons';

import { RESOLVE_EFFECTIVE_CC_SCORE } from '@shared/utils/quality-data-utils';

import { ProjectDetailsStore } from '../store/project-details.store';
import { projectDetailsPageStateKey, ProjectDetailsState } from '../state';

import {
	PROJECT_DETAILS,
	PROJECT_CARRIER_MAP,
	PROJECT_GROUPS,
	RECOMMENDED_CARRIERS,
} from '../actions';

import { getProjectsFilters } from '../../../v2/utils/projects-list';
import { buildRequest } from '../../../v2/procurement/procurement-list/build-projects-request';
import { AllocationService } from '@core/services/allocation/allocation.service';

// CN-8785: upper bound (ms) for the single project GET so a hung request under load
// resolves to a FAILURE (which resets loadingProject and shows an error) instead of
// leaving the project details blank with no message.
const PROJECT_LOAD_TIMEOUT_MS = 60000;

@Injectable({
	providedIn: 'root',
})
export class ProjectDetailsEffects {
	constructor(
		private actions$: Actions<any>,
		private readonly store: Store<State>,
		public readonly projectDetailsStore: ProjectDetailsStore,
		private readonly restService: RestService,
		private readonly urlService: UrlService,
		public utilService: UtilService,
		private dialog: MatDialog,
		public projectHelperService: ProjectHelperService,
		public cargochief: Cargochief,
		private projectDetailsService: ProjectDetailsService,
		public accountSettings: AccountSettingsService,
		private capacitySearchFilterService: CapacitySearchFilterService,
		private csvExportService: CsvExportService,
		private allocationService: AllocationService
	) {}

	// Project whose carrier maps were last re-split by recomputeCarrierGroupsOnProjectLoad$.
	private groupSplitProjectId: string = null;

	getProject$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_DETAILS.PROJECT.GET),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const url = this.urlService.getProjectById(projectDetailsPageState.projectId);
				return this.restService.GET(url).pipe(
					timeout(PROJECT_LOAD_TIMEOUT_MS),
					map((response: any) => {
						let projectDTO = this.projectHelperService.convertProjectCentsToDollars(response.data);
						let searchTruckForm: any = this.projectDetailsService.getSearchTruckForm(projectDTO);
						const frequencyFilter = this.cargochief.getItem(CARGOCHIEF_KEYS.FREQUENCY_FILTER);
						if (IS_NOT_NULL_OR_UNDEFINED(frequencyFilter)) {
							const { minLoadsPerWeek } = JSON.parse(frequencyFilter);
							searchTruckForm.minLoadsPerWeek = minLoadsPerWeek;
						}
						this.store.dispatch(
							PROJECT_DETAILS.PROJECT.SET_CAPACITY_SEARCH_FORM({ searchTruckForm: searchTruckForm })
						);
						const customerOnlyProject =
							IS_EMPTY(projectDTO.originCity) &&
							IS_EMPTY(projectDTO.originState) &&
							IS_EMPTY(projectDTO.destCity) &&
							IS_EMPTY(projectDTO.destState) &&
							IS_NOT_NULL_OR_UNDEFINED(projectDTO.customerDTO);
						this.store.dispatch(
							PROJECT_DETAILS.SET.CUSTOMER_ONLY_PROJECT({
								customerOnlyProject: customerOnlyProject,
							})
						);
						return PROJECT_DETAILS.PROJECT.SUCCESS({ projectDTO });
					}),
					catchError((error) => {
						console.error('getProject failed: ' + error);
						this.utilService.showSnackbar(
							'We could not load this project. Please refresh to try again.',
							SNACKBAR.ERROR,
							SNACKBAR.ACTION
						);
						return of(PROJECT_DETAILS.PROJECT.FAILURE({ payload: { message: error?.error } }));
					})
				);
			})
		)
	);

	getAllProjects$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_DETAILS.PROJECT.GET_ALL),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const filteredFilters = getProjectsFilters(
					[],
					this.accountSettings,
					this.utilService.isUserRoleAdmin(),
					this.cargochief.USER_ID
				);
				let requestDTO = buildRequest(
					this.cargochief.ACCOUNT_ID,
					this.accountSettings.ANNUAL_VOLUME,
					filteredFilters || [],
					projectDetailsPageState.projectsPage,
					projectDetailsPageState.projectsPageSize,
					'created',
					'desc',
					[],
					this.cargochief.USER_ID,
					[],
					[]
				);

				return this.restService.POST(this.urlService.getProjectsList(), requestDTO).pipe(
					map((response: any) => {
						this.store.dispatch(
							PROJECT_DETAILS.PROJECT.SET_ALL_PROJECTS_LIST({
								list: projectDetailsPageState.allProjectsList.concat(response.data),
							})
						);
						this.store.dispatch(
							PROJECT_DETAILS.PROJECT.SET_PROJECTS_NEXT_PAGE({
								projectsNextPage: response.nextPage,
							})
						);
						if (response.nextPage) {
							this.store.dispatch(
								PROJECT_DETAILS.PROJECT.SET_PROJECTS_PAGE({
									page: projectDetailsPageState.projectsPage + 1,
								})
							);
						}
						this.store.dispatch(
							PROJECT_DETAILS.PROJECT.SET_TOTAL_PROJECTS({ totalElements: response.totalElements })
						);
						return PROJECT_DETAILS.PROJECT.SUCCESS({});
					}),
					catchError((error) =>
						of(PROJECT_DETAILS.PROJECT.FAILURE({ payload: { message: error.error } }))
					)
				);
			})
		)
	);

	setEngagedCarriers(projectCarrierMapsList) {
		// engaged carriers
		let engagedCarriersList = [];
		if (environment.ENABLE_ENGAGED_TAB_ON_PROJECT_DETAILS) {
			engagedCarriersList = projectCarrierMapsList.filter(
				(pcm) => pcm.status === PROJECT_CARRIER_MAP_STATUS.IN_PROGRESS
			);
		}
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.ENGAGED_CARRIERS_COUNT({
				total: engagedCarriersList.length,
			})
		);
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.ENGAGED_CARRIERS_LIST({
				list: engagedCarriersList,
			})
		);
	}

	// Procurement projects ignore any projectGroup data the API returns. The type only counts once
	// the loaded project is the one being viewed: carrier maps are fetched first, and on
	// navigation the previous project's DTO is still in the store. Until then keep today's
	// grouping behaviour — suppressing groups on an unknown type would render a Waterfall
	// project's tiers as ungrouped. recomputeCarrierGroupsOnProjectLoad$ re-splits once the
	// right project lands.
	useProjectGroups(): boolean {
		let useGroups = true;
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(take(1))
			.subscribe((state) => {
				const projectTypeIsKnown = state.projectDTO?.id === state.projectId;
				useGroups = !projectTypeIsKnown || IS_WORKFLOW_PROJECT_TYPE(state.projectDTO?.type);
			});
		return useGroups;
	}

	// PROJECT_CARRIER_MAP.GET_ALL is dispatched before PROJECT.GET, so the carrier maps can be
	// split before the project type is known. Re-split once the project lands.
	//
	// Scoped to the first SUCCESS per project on purpose. PROJECT.SUCCESS is also dispatched on
	// every project save, and re-splitting there would (a) rebuild the groups from the stale
	// projectGroup copies embedded in allProjectCarrierMapsList, reverting a group reorder, and
	// (b) empty state.projectGroups before deleteProjectGroups$ reads it to null out
	// pcm.projectGroup. Type changes are already handled by the delete/create group flows in
	// updateProject.
	recomputeCarrierGroupsOnProjectLoad$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(PROJECT_DETAILS.PROJECT.SUCCESS),
				withLatestFrom(this.store.select(projectDetailsPageStateKey)),
				map(([action, state]: [any, ProjectDetailsState]) =>
					this.recomputeCarrierGroupsForLoadedProject(action.projectDTO, state)
				)
			),
		{ dispatch: false }
	);

	recomputeCarrierGroupsForLoadedProject(projectDTO: ProjectDTO, state: ProjectDetailsState) {
		const loadedProjectId = projectDTO?.id;
		// Drop a success that lands after the user has navigated away — re-splitting the active
		// project's carrier maps against another project's type would be wrong.
		if (
			!loadedProjectId ||
			loadedProjectId !== state.projectId ||
			loadedProjectId === this.groupSplitProjectId
		) {
			return;
		}
		this.groupSplitProjectId = loadedProjectId;
		if (state.allProjectCarrierMapsList?.length) {
			this.setProcuredCarriers(state.allProjectCarrierMapsList);
			this.setProjectGroups(state.allProjectCarrierMapsList);
		}
	}

	setProcuredCarriers(projectCarrierMapsList) {
		const useGroups = this.useProjectGroups();
		let procuredCarriersList = [];
		procuredCarriersList = projectCarrierMapsList.filter(
			(pcm) =>
				pcm.status === PROJECT_CARRIER_MAP_STATUS.PROCURED &&
				(!useGroups || IS_NULL_OR_UNDEFINED(pcm.projectGroup))
		);
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.PROCURED_CARRIERS_LIST({
				list: procuredCarriersList,
			})
		);
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.PROCURED_CARRIERS_COUNT({
				total: projectCarrierMapsList.filter(
					(pcm) => pcm.status === PROJECT_CARRIER_MAP_STATUS.PROCURED
				).length,
			})
		);
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.PROCURED_CARRIERS_WITHOUT_COUNT({
				total: procuredCarriersList.length,
			})
		);
	}

	setProjectGroups(projectCarrierMapsList) {
		if (!this.useProjectGroups()) {
			this.store.dispatch(PROJECT_GROUPS.SET.PROJECT_GROUPS({ list: [] }));
			return;
		}

		let projectGroups = [];
		const projectsWithGroup = projectCarrierMapsList.filter(
			(pcm) =>
				pcm.status === PROJECT_CARRIER_MAP_STATUS.PROCURED &&
				IS_NOT_NULL_OR_UNDEFINED(pcm.projectGroup)
		);

		projectGroups = projectsWithGroup
			.map((pcm) => pcm.projectGroup)
			.filter(
				(value: ProjectGroup, index, self) => index === self.findIndex((t) => t.id === value.id)
			)
			.map((pcm) => Object.assign({}, pcm))
			.sort((a, b) => {
				// "Allocated Carriers" group always goes last, regardless of delayByMinutes
				if (a.name === ALLOCATED_CARRIERS_GROUP_NAME) return 1;
				if (b.name === ALLOCATED_CARRIERS_GROUP_NAME) return -1;
				// All other groups sort by delayByMinutes
				return a.delayByMinutes > b.delayByMinutes ? 1 : -1;
			});

		projectGroups.forEach((group) => {
			let groupPCMList = projectsWithGroup
				.filter((pcm) => pcm.projectGroup?.id === group.id)
				.map((pcm) => Object.assign({}, pcm));

			group.projectCarrierMapsList = groupPCMList;
			group.numberOfCarriers = group.projectCarrierMapsList.length;
			group.trucksCount = group.projectCarrierMapsList
				.map((pcm) => pcm.carrier.powerUnits || 0)
				.reduce((prev, next) => prev + next);
			group.lanesCount = group.projectCarrierMapsList
				.map((pcm) => pcm.carrier.laneCount || 0)
				.reduce((prev, next) => prev + next);
		});

		this.store.dispatch(
			PROJECT_GROUPS.SET.PROJECT_GROUPS({
				list: projectGroups,
			})
		);
	}

	setCapacitySearchMatchesList(truckSearchResult) {
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(take(1))
			.subscribe((state) => {
				let capacitySearchResults = this.getAllUniqueCapacitySearchResults(
					state.allCapacitySearchResults,
					state.allProjectCarrierMapsList,
					state.powerUnits
				);
				capacitySearchResults = capacitySearchResults.splice(
					0,
					environment.NO_OF_SEARCH_RESULTS_TO_DISPLAY
				);
				this.store.dispatch(
					RECOMMENDED_CARRIERS.SET.TRUCK_SEARCH_RESULT({ list: truckSearchResult })
				);

				this.setCapacitySearchResults(capacitySearchResults);
			});
	}

	createOrUpdateSystemGroup(projectCarrierMapIds: string[]) {
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(take(1))
			.subscribe((state) => {
				if (projectCarrierMapIds.length) {
					// add carrier maps to system group if exist or else create system group
					this.store
						.select((state) => state[projectDetailsPageStateKey])
						.pipe(take(1))
						.subscribe((state) => {
							let existingSystemGroup = state.projectGroups.find((group) => group.systemGroup);
							if (existingSystemGroup) {
								let delayByMinutes = existingSystemGroup.delayByMinutes;
								const projectGroups = state.projectGroups.filter((group) => !group.systemGroup);
								if (projectGroups.length > 0) {
									delayByMinutes =
										(projectGroups[projectGroups.length - 1]?.delayByMinutes || 0) +
										DEFAULT_SYSTEM_GROUP_DELAY;
								}
								this.store.dispatch(
									PROJECT_GROUPS.UPDATE({
										systemGroup: true,
										projectCarrierMapIdList: projectCarrierMapIds.concat(
											existingSystemGroup.projectCarrierMapsList.map((pcm) => pcm.id)
										),
										name: existingSystemGroup.name,
										projectId: state.projectId,
										delayByMinutes,
										projectGroupId: existingSystemGroup.id,
									})
								);
							} else {
								// delayByMinutes should be 30 mins after lowestPriority group
								const delayByMinutes =
									(state.projectGroups[state.projectGroups.length - 1]?.delayByMinutes || 0) +
									DEFAULT_SYSTEM_GROUP_DELAY;
								const systemGroupRequest = {
									systemGroup: true,
									projectCarrierMapIdList: projectCarrierMapIds,
									name: DEFAULT_SYSTEM_GROUP_NAME,
									projectId: state.projectId,
									delayByMinutes,
								};
								this.store.dispatch(PROJECT_GROUPS.CREATE(systemGroupRequest));
							}
						});
				}
			});
	}

	sortSearchList(sortColumn: string) {
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(take(1))
			.subscribe((state) => {
				let capacitySearchResults = [].concat(state.capacitySearchResults);
				capacitySearchResults = this.capacitySearchFilterService.sortByColumn(
					sortColumn,
					capacitySearchResults
				);
				this.store.dispatch(
					RECOMMENDED_CARRIERS.SET.CAPACITY_SEARCH_RESULT({ list: capacitySearchResults })
				);
			});
	}

	filterCarriers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_CARRIER_MAP.FILTER.APPLY),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				let powerUnits = projectDetailsPageState.powerUnits,
					minimumFrequency = projectDetailsPageState.minLoadsPerWeek,
					myCarriers = projectDetailsPageState.myCarriers,
					ratesOnly = projectDetailsPageState.ratesOnly,
					mc = projectDetailsPageState.carrierMc,
					dot = projectDetailsPageState.carrierDot,
					// CN-8312: bare numeric MC/DOT input (e.g. value copied from a
					// carrier card). Match against MC OR DOT instead of AND'ing both.
					mcOrDot = projectDetailsPageState.carrierMcOrDot,
					businessName = projectDetailsPageState.carrierBusinessName,
					outOfNetworkCarriers = projectDetailsPageState.outOfNetworkCarriers,
					qualityFilter = projectDetailsPageState.qualityFilter;
				let projectCarrierMapsList = projectDetailsPageState.allProjectCarrierMapsList.filter(
						(pcm) => pcm.carrier.powerUnits >= powerUnits
					),
					capacitySearchResults = this.getAllUniqueCapacitySearchResults(
						projectDetailsPageState.allCapacitySearchResults,
						projectDetailsPageState.allProjectCarrierMapsList,
						projectDetailsPageState.powerUnits
					);
				if (projectDetailsPageState.carrierHQ) {
					capacitySearchResults = capacitySearchResults.filter(
						(search) => projectDetailsPageState.matchedCarrierHqIds.indexOf(search.carrierId) !== -1
					);
				}

				if (myCarriers) {
					projectCarrierMapsList = projectCarrierMapsList.filter(
						(pcm) => pcm.carrier.customerCarrier
					);
					capacitySearchResults = capacitySearchResults.filter((search) => search.customerCarrier);
				}
				if (outOfNetworkCarriers) {
					projectCarrierMapsList = projectCarrierMapsList.filter(
						(pcm) => !pcm.carrier.customerCarrier
					);
					capacitySearchResults = capacitySearchResults.filter((search) => !search.customerCarrier);
				}
				if (ratesOnly) {
					projectCarrierMapsList = projectCarrierMapsList.filter((pcm) => pcm.desiredPrice > 0);
				}
				if (IS_NOT_EMPTY(mc)) {
					projectCarrierMapsList = projectCarrierMapsList.filter(
						(pcm) =>
							IS_NOT_NULL_OR_UNDEFINED(pcm.carrier.mc) &&
							pcm.carrier.mc.toLowerCase().includes(mc.toLowerCase())
					);
					capacitySearchResults = capacitySearchResults.filter(
						(search) =>
							IS_NOT_NULL_OR_UNDEFINED(search.firmMc) &&
							search.firmMc.toLowerCase().includes(mc.toLowerCase())
					);
				}
				if (IS_NOT_EMPTY(dot)) {
					projectCarrierMapsList = projectCarrierMapsList.filter(
						(pcm) =>
							IS_NOT_NULL_OR_UNDEFINED(pcm.carrier.dot) &&
							pcm.carrier.dot.toLowerCase().includes(dot.toLowerCase())
					);
					capacitySearchResults = capacitySearchResults.filter(
						(search) =>
							IS_NOT_NULL_OR_UNDEFINED(search.firmDot) &&
							search.firmDot.toLowerCase().includes(dot.toLowerCase())
					);
				}
				// CN-8312: OR match against MC or DOT for bare numeric search input.
				// Used when the user pastes a value copied from a card without an
				// `mc:` / `dot:` prefix — we don't know which field they meant, so
				// match either.
				if (IS_NOT_EMPTY(mcOrDot)) {
					const v = mcOrDot.toLowerCase();
					projectCarrierMapsList = projectCarrierMapsList.filter((pcm) => {
						const pcmMc = pcm.carrier.mc?.toLowerCase();
						const pcmDot = pcm.carrier.dot?.toLowerCase();
						return (pcmMc && pcmMc.includes(v)) || (pcmDot && pcmDot.includes(v));
					});
					capacitySearchResults = capacitySearchResults.filter((search) => {
						const sMc = search.firmMc?.toLowerCase();
						const sDot = search.firmDot?.toLowerCase();
						return (sMc && sMc.includes(v)) || (sDot && sDot.includes(v));
					});
				}
				if (IS_NOT_EMPTY(businessName)) {
					projectCarrierMapsList = projectCarrierMapsList.filter(
						(pcm) =>
							IS_NOT_NULL_OR_UNDEFINED(pcm.carrier.businessName) &&
							pcm.carrier.businessName.toLowerCase().includes(businessName.toLowerCase())
					);
					capacitySearchResults = capacitySearchResults.filter(
						(search) =>
							IS_NOT_NULL_OR_UNDEFINED(search.businessName) &&
							search.businessName.toLowerCase().includes(businessName.toLowerCase())
					);
				}

				if (IS_NOT_EMPTY(qualityFilter)) {
					const qualityLevelUpper = qualityFilter.toUpperCase();
					const accountId = this.cargochief.ACCOUNT_ID;

					projectCarrierMapsList = projectCarrierMapsList.filter((pcm) => {
						if (
							!pcm.carrier ||
							!pcm.carrier.accountCarrierMaps ||
							pcm.carrier.accountCarrierMaps.length === 0
						) {
							return false;
						}

						// CN-8744: shared resolution (account-scoped ACM score → carrier-level
						// fallback) so the quality filter and the quality display always agree.
						const ccScore = RESOLVE_EFFECTIVE_CC_SCORE(pcm.carrier, accountId);

						if (IS_NULL_OR_UNDEFINED(ccScore)) {
							return false;
						}

						if (qualityLevelUpper === QUALITY_SCORE_DISPLAY.HIGH.toUpperCase()) {
							return ccScore > environment.HIGH_QUALITY;
						} else if (qualityLevelUpper === QUALITY_SCORE_DISPLAY.MEDIUM.toUpperCase()) {
							return ccScore > environment.MEDIUM_QUALITY && ccScore <= environment.HIGH_QUALITY;
						} else if (qualityLevelUpper === QUALITY_SCORE_DISPLAY.LOW.toUpperCase()) {
							return ccScore <= environment.MEDIUM_QUALITY;
						}
						return false;
					});

					capacitySearchResults = capacitySearchResults.filter((search) => {
						if (IS_NULL_OR_UNDEFINED(search.ccScore)) {
							return false;
						}
						const ccScore = search.ccScore;

						if (qualityLevelUpper === QUALITY_SCORE_DISPLAY.HIGH.toUpperCase()) {
							return ccScore > environment.HIGH_QUALITY;
						} else if (qualityLevelUpper === QUALITY_SCORE_DISPLAY.MEDIUM.toUpperCase()) {
							return ccScore > environment.MEDIUM_QUALITY && ccScore <= environment.HIGH_QUALITY;
						} else if (qualityLevelUpper === QUALITY_SCORE_DISPLAY.LOW.toUpperCase()) {
							return ccScore <= environment.MEDIUM_QUALITY;
						}
						return false;
					});
				}

				this.setProcuredCarriers(projectCarrierMapsList);
				this.setEngagedCarriers(projectCarrierMapsList);
				this.setProjectGroups(projectCarrierMapsList);

				this.setCapacitySearchResults(capacitySearchResults);

				return of(PROJECT_CARRIER_MAP.FILTER.COMPLETE({}));
			})
		)
	);

	setCapacitySearchResults(capacitySearchResults: TruckSearchTable[]) {
		this.store.dispatch(
			RECOMMENDED_CARRIERS.SET.CAPACITY_SEARCH_RESULT({ list: capacitySearchResults })
		);
		this.store.dispatch(
			RECOMMENDED_CARRIERS.SET.NUMBER_OF_LANES({ count: capacitySearchResults.length })
		);
		this.store.dispatch(
			RECOMMENDED_CARRIERS.SET.BEST_MATCH({
				bestMatch: this.capacitySearchFilterService.getMaxMatch(capacitySearchResults),
			})
		);
	}

	getAllUniqueCapacitySearchResults(
		allCapacitySearchResults: TruckSearchTable[],
		allProjectCarrierMapsList: ProjectCarrierMapDTO[],
		powerUnits
	) {
		let capacitySearchResults = allCapacitySearchResults.filter(
			(search) => search.powerUnits >= powerUnits
		);
		const carrierIdsInProject: string[] = allProjectCarrierMapsList.map((pcm) => pcm.carrier.id);
		capacitySearchResults = capacitySearchResults.filter(
			(search) => carrierIdsInProject.indexOf(search.carrierId) == -1
		);
		return capacitySearchResults;
	}

	moveCarriersToGroupsWhenCarriersProcured(
		projectDetailsPageState: ProjectDetailsState,
		procuredCarriersResponse: ProjectCarrierMapDTO[]
	) {
		// Procurement projects have no groups to move carriers into.
		if (!IS_WORKFLOW_PROJECT_TYPE(projectDetailsPageState.projectDTO?.type)) {
			return;
		}
		// check if any group  exist
		const isGroupExist: boolean = projectDetailsPageState.projectGroups.length > 0;
		if (isGroupExist) {
			// add new carriers added to system group
			const systemGroup = projectDetailsPageState.projectGroups.find((group) => group.systemGroup);
			this.createOrUpdateSystemGroup(procuredCarriersResponse.map((pcm) => pcm.id));
		} else {
			let highestPriorityGroupRequest = {
				projectCarrierMapIdList: procuredCarriersResponse.map((pcm) => pcm.id),
				name: 'Priority 1',
				projectId: projectDetailsPageState.projectId,
				delayByMinutes: [].concat(DELAY_OPTIONS).sort((a, b) => (a.value > b.value ? 1 : -1))[0]
					.value,
			};
			// no group exists yet, so create the highest priority group
			this.store.dispatch(
				PROJECT_GROUPS.GROUP_AND_DEFAULT_GROUP.CREATE(highestPriorityGroupRequest)
			);
		}
	}

	async setCapacitySearchDataForProjectCarrierMap() {
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(take(1), debounceTime(200))
			.subscribe((state) => {
				if (state.allCapacitySearchResults.length && state.allProjectCarrierMapsList.length) {
					let allProjectCarrierMapsList = state.allProjectCarrierMapsList.map((pcm) =>
						Object.assign({}, pcm)
					);
					allProjectCarrierMapsList.forEach((pcm) => {
						if (IS_EMPTY(pcm.frequency)) {
							const searchTruck = state.allCapacitySearchResults.find(
								(search) => search.carrierId === pcm.carrier.id
							);
							if (searchTruck) {
								pcm.frequency = searchTruck.frequency;
								pcm.originDeadhead = searchTruck.originDeadhead;
								pcm.destinationDeadhead = searchTruck.destinationDeadhead;
								pcm.lanePath = BUILD_LANE(
									FORMAT_LOCATION(searchTruck.originCity, searchTruck.originState, null),
									FORMAT_LOCATION(searchTruck.destCity, searchTruck.destState, null),
									searchTruck.trailerTypes
								);
								pcm.matchScore = searchTruck.match;
								// CN-8312: only inherit recentFeedbackDate from the search truck when
								// the pcm doesn't already have one. Without this guard, freshly engaged
								// carriers had their just-written feedback date clobbered by the
								// search truck's null value (since the search truck was built from
								// pre-engage data and never includes the new feedback row).
								if (
									IS_NULL_OR_UNDEFINED(pcm.recentFeedbackDate) &&
									IS_NOT_NULL_OR_UNDEFINED(searchTruck.recentFeedbackDate)
								) {
									pcm.recentFeedbackDate = searchTruck.recentFeedbackDate;
								}
							}
						}
					});
					this.setProcuredCarriers(allProjectCarrierMapsList);
					this.setEngagedCarriers(allProjectCarrierMapsList);
					this.setProjectGroups(allProjectCarrierMapsList);
					this.store.dispatch(
						PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
							list: allProjectCarrierMapsList,
						})
					);
				}
			});
	}

	openBADialog$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_DETAILS.BA.OPEN_DIALOG),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request) => {
				const projectDTO = request[1].projectDTO;
				const { lanePricing, greenScreenRate } = request[0];
				let accountCarrierMapCount: any = this.cargochief.getItem(
					CARGOCHIEF_KEYS.ACCOUNT_CARRIER_MAP_COUNT
				);

				this.projectDetailsService.openBADialog(accountCarrierMapCount, projectDTO, {
					lanePricing,
					greenScreenRate,
				});
				return of(PROJECT_DETAILS.BA.CLOSE_DIALOG({}));
			})
		)
	);

	downloadEngagedCarriersCSV$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_DETAILS.DOWNLOAD.ENGAGEED_CARRIERS),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			switchMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				let engagedCarriersList = projectDetailsPageState.engagedCarriersList;
				if (engagedCarriersList.length) {
					let result = [];
					let carriersWithoutContact = 0;
					const currentPcms = this.indexCurrentPcmsById(projectDetailsPageState);
					engagedCarriersList.forEach((projectCarrierMap) => {
						let carrier = projectCarrierMap.carrier;
						const contactToExport = GET_PROJECT_EXPORT_CONTACT(
							currentPcms.get(projectCarrierMap.id) || projectCarrierMap,
							carrier
						);
						if (!contactToExport) {
							carriersWithoutContact++;
						}

						let row = [
							carrier.businessName,
							carrier.dot,
							carrier.mc,
							...this.getContactColumns(contactToExport),
						];
						result.push(row);
					});
					const baseName = `${GET_LANE_PATH_STRING(projectDetailsPageState.projectDTO).replace(/→/g, '-')} Engaged Carriers`,
						fileHeaders = ['Carrier', 'DOT', 'MC', 'Contact Name', 'Phone', 'Email'];
					this.csvExportService.downloadCsvFile(baseName, fileHeaders, result);
					this.utilService.showSnackbar(
						this.getExportSnackbarMessage(baseName, carriersWithoutContact),
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
				}

				return of(PROJECT_DETAILS.DOWNLOAD.SUCCCESS({}));
			})
		)
	);

	downloadProcuredCarriersCSV$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_DETAILS.DOWNLOAD.PROCURED_CARRIERS),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			switchMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				// Grouped + ungrouped, both narrowed by any active filter
				let procuredCarriersList = [];
				projectDetailsPageState.projectGroups.forEach((group) => {
					procuredCarriersList = procuredCarriersList.concat(group.projectCarrierMapsList);
				});
				procuredCarriersList = procuredCarriersList.concat(
					projectDetailsPageState.procuredCarriersList
				);
				if (procuredCarriersList.length) {
					let result = [];
					let carriersWithoutContact = 0;
					const currentPcms = this.indexCurrentPcmsById(projectDetailsPageState);
					procuredCarriersList.forEach((projectCarrierMap) => {
						let carrier = projectCarrierMap.carrier;
						const contactToExport = GET_PROJECT_EXPORT_CONTACT(
							currentPcms.get(projectCarrierMap.id) || projectCarrierMap,
							carrier
						);
						if (!contactToExport) {
							carriersWithoutContact++;
						}

						let row = [
							carrier.businessName,
							carrier.dot,
							carrier.mc,
							...this.getContactColumns(contactToExport),
							projectCarrierMap?.projectGroup?.name || 'N/A',
						];
						result.push(row);
					});
					const baseName = `${GET_LANE_PATH_STRING(projectDetailsPageState.projectDTO).replace(/→/g, '-')} Procured Carriers`,
						fileHeaders = ['Carrier', 'DOT', 'MC', 'Contact Name', 'Phone', 'Email', 'Group'];
					this.csvExportService.downloadCsvFile(baseName, fileHeaders, result);
					this.utilService.showSnackbar(
						this.getExportSnackbarMessage(baseName, carriersWithoutContact),
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
				}

				return of(PROJECT_DETAILS.DOWNLOAD.SUCCCESS({}));
			})
		)
	);

	// The filtered/grouped lists hold copies that CONTACT_UPDATE never refreshes, so contacts have
	// to be read off allProjectCarrierMapsList instead.
	private indexCurrentPcmsById(
		projectDetailsPageState: ProjectDetailsState
	): Map<string, ProjectCarrierMapDTO> {
		return new Map(
			(projectDetailsPageState.allProjectCarrierMapsList || []).map((pcm) => [pcm.id, pcm])
		);
	}

	// Contact Name / Phone / Email columns, blank when the carrier has no contact on file
	private getContactColumns(contact: any): Array<string> {
		if (!contact) {
			return ['', '', ''];
		}
		const phone = IS_NOT_NULL_OR_UNDEFINED(contact.mobile)
			? contact.mobile
			: contact.phone || contact.phoneNumber;
		const fullName =
			contact.fullName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
		return [fullName, phone || '', contact.emailAddress || contact.email || ''];
	}

	private getExportSnackbarMessage(baseName: string, carriersWithoutContact: number): string {
		const message = `File ${baseName}.csv saved successfully`;
		if (!carriersWithoutContact) {
			return message;
		}
		const carriers = carriersWithoutContact === 1 ? 'carrier' : 'carriers';
		return `${message} — ${carriersWithoutContact} ${carriers} had no contact on file`;
	}

	downloadAllocatedCarriersCSV$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_DETAILS.DOWNLOAD.ALLOCATED_CARRIERS),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			switchMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const allocationsMap = projectDetailsPageState.carrierAllocationsMap || {};

				// Filter to only carriers with allocations
				const allocatedCarriersList = (
					projectDetailsPageState.allProjectCarrierMapsList || []
				).filter((pcm) => allocationsMap[pcm.carrier?.id]);

				if (allocatedCarriersList.length) {
					let result = [];
					let carriersWithoutContact = 0;
					allocatedCarriersList.forEach((projectCarrierMap) => {
						let carrier = projectCarrierMap.carrier;
						const allocation = allocationsMap[carrier?.id];
						const contactToExport = GET_PROJECT_EXPORT_CONTACT(projectCarrierMap, carrier);
						if (!contactToExport) {
							carriersWithoutContact++;
						}

						// Format allocation info based on type
						let allocationInfo = 'N/A';
						if (allocation) {
							if (allocation.allocationType === 'PERCENTAGE') {
								allocationInfo = `${allocation.allocationValue}%`;
							} else {
								allocationInfo = `${allocation.allocationValue} loads/${allocation.allocationPeriod?.toLowerCase() || 'period'}`;
							}
						}

						let row = [
							carrier.businessName,
							carrier.dot,
							carrier.mc,
							...this.getContactColumns(contactToExport),
							allocationInfo,
							projectCarrierMap?.projectGroup?.name || 'N/A',
						];
						result.push(row);
					});
					const baseName = `${GET_LANE_PATH_STRING(projectDetailsPageState.projectDTO).replace(/→/g, '-')} Allocated Carriers`,
						fileHeaders = [
							'Carrier',
							'DOT',
							'MC',
							'Contact Name',
							'Phone',
							'Email',
							'Allocation',
							'Group',
						];
					this.csvExportService.downloadCsvFile(baseName, fileHeaders, result);
					this.utilService.showSnackbar(
						this.getExportSnackbarMessage(baseName, carriersWithoutContact),
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
				}

				return of(PROJECT_DETAILS.DOWNLOAD.SUCCCESS({}));
			})
		)
	);

	async getMaxBuyExceedConfirmation(desiredPrice: number, maxBuy: number, businessName: string) {
		let result = true;
		if (
			IS_NOT_NULL_OR_UNDEFINED(desiredPrice) &&
			IS_NOT_NULL_OR_UNDEFINED(maxBuy) &&
			desiredPrice > TO_CENTS(maxBuy)
		) {
			result = await this.dialog
				.open(ConfirmationDialogComponent, {
					width: '500px',
					data: {
						title: 'Confirm Change',
						body: `The rate $${TO_DOLLARS(desiredPrice).toLocaleString()} exceeds the project max buy of $${maxBuy.toLocaleString()}. All offers to ${businessName} will use the max buy unless the max buy is increased.`,
						cancelButtonText: 'Cancel',
						ConfirmButtonText: 'Confirm',
					},
					disableClose: true,
				})
				.afterClosed()
				.toPromise();
		}
		return result;
	}

	/**
	 * Effect to load carrier allocations for a project.
	 * Triggered when navigating to a workflow project or when allocations are updated.
	 */
	loadCarrierAllocations$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_DETAILS.ALLOCATIONS.LOAD),
			switchMap((action: { projectId: string }) =>
				this.allocationService.getAllocationsForProject(action.projectId).pipe(
					map((allocations) => PROJECT_DETAILS.ALLOCATIONS.LOAD_SUCCESS({ allocations })),
					catchError((error) =>
						of(PROJECT_DETAILS.ALLOCATIONS.LOAD_FAILURE({ error: error.message }))
					)
				)
			)
		)
	);

	/**
	 * Effect to delete a carrier allocation.
	 * After successful deletion, reloads the allocations for the project.
	 */
	deleteCarrierAllocation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_DETAILS.ALLOCATIONS.DELETE),
			switchMap((action: { allocationId: string; projectId: string }) =>
				this.allocationService.deleteAllocation(action.allocationId).pipe(
					map(() =>
						PROJECT_DETAILS.ALLOCATIONS.DELETE_SUCCESS({
							allocationId: action.allocationId,
							projectId: action.projectId,
						})
					),
					catchError((error) =>
						of(PROJECT_DETAILS.ALLOCATIONS.DELETE_FAILURE({ error: error.message }))
					)
				)
			)
		)
	);

	/**
	 * Effect to reload allocations after a successful deletion.
	 */
	reloadAllocationsAfterDelete$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_DETAILS.ALLOCATIONS.DELETE_SUCCESS),
			map((action: { projectId: string }) =>
				PROJECT_DETAILS.ALLOCATIONS.LOAD({ projectId: action.projectId })
			)
		)
	);
}
